import { Component, inject, OnInit, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Observable } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { TimeTrack } from 'src/app/entities/time-track/time-track';
import { StoreService } from 'src/app/services/store-service/store.service';
import { Project } from 'src/app/entities/project/project';
import { Tag } from 'src/app/entities/tag/tag';
import { User } from 'src/app/entities/user/user';
import { FiltersService } from 'src/app/services/filters-service/filters.service';
import { Roles, UserRepository } from 'src/app/services/remote-data-service/repositories/user-repository/user.repository';
import { MatDialog } from '@angular/material/dialog';
import { TimeEntryInputComponent } from 'src/app/components/atoms/time-entry-input/time-entry-input.component';
import { TrackListComponent } from 'src/app/components/molecules/track-list/track-list.component';
import { ButtonToggleItem, ButtonTogglerComponent } from 'src/app/components/atoms/button-toggler/button-toggler.component';
import { TimeEntryPlayerComponent } from 'src/app/components/atoms/time-entry-player/time-entry-player.component';
import { EntityItemsAnnihilatorComponent } from 'src/app/components/atoms/mutations/entity-items-annihilator/entity-items-annihilator.component';


enum timeEntriesTypes {
  MANUAL = 'manual',
  TIMER = 'timer'
}


/**
 * Main page component for time tracking functionality.
 * Manages the time entry input and list of tracked time entries.
 * Handles data flow between the store and child components.
 */
@Component({
  selector: 'app-tracker-page',
  templateUrl: './tracker-page.component.html',
  styleUrls: ['./tracker-page.component.scss'],
  imports: [
    TimeEntryInputComponent,
    TrackListComponent,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    ButtonTogglerComponent,
    TimeEntryPlayerComponent
  ]
})
export class TrackerPageComponent implements OnInit, AfterViewInit {

  readonly dialog = inject(MatDialog);

  /** Local array of time tracking entries */
  trackEntries: TimeTrack[] = [];
  /** Local array of available projects */
  projects: Project[] = [];
  /** Local array of available tags */
  tags: Tag[] = [];
  /** Local array of available users */
  users: User[] = [];

  /** Observable stream of time tracks from store */
  tracks$: Observable<TimeTrack[]>;
  /** Observable stream of projects from store */
  projects$: Observable<Project[]>;
  /** Observable stream of tags from store */
  tags$: Observable<Tag[]>;
  /** Observable stream of users from store */
  users$: Observable<User[]>;
  /** Observable stream of the current timer player time track */
  timerPlayerResumer$: Observable<TimeTrack | null>;

  public form = new FormGroup({
    title: new FormControl<string>(''),
    members: new FormControl<string[]>([])
  });

  buttons: ButtonToggleItem[] = [
    {
      name: 'Manual',
      value: 'manual'
    },
    {
      name: 'Timer',
      value: 'timer'
    }
  ]

  trackingEntryInCourse?: TimeTrack | null;

  isManager = false;

  userId?: string;

  timeEntrySelected = timeEntriesTypes.MANUAL

  constructor(
    protected store: StoreService,
    protected filters: FiltersService,
    protected userRep: UserRepository,
    private cdr: ChangeDetectorRef
  ) {
    this.projects$ = this.store.projects;
    this.tags$ = this.store.tags;
    this.tracks$ = this.store.timeTracks;
    this.users$ = this.store.users;
    this.timerPlayerResumer$ = this.store.timerPlayerResume;

    this.tracks$.subscribe((tracks) => {
      let trackInCourse: TimeTrack | null = null;

      this.trackEntries.forEach((track) => {
        if (track.isOngoing() && track.author?.id === this.userId) {
          trackInCourse = track;
        }
      })

      if (trackInCourse) {
        this.trackingEntryInCourse = trackInCourse
        this.timeEntrySelected = timeEntriesTypes.TIMER
      } else this.trackingEntryInCourse = null

      this.trackEntries = tracks
    });

    // These subscriptions can happen in parallel
    this.projects$.subscribe((projects) => {
      this.projects = projects;
    });

    this.tags$.subscribe((tags) => {
      this.tags = tags;
    });

    this.users$.subscribe((users) => {
      this.users = users;
    });

    this.timerPlayerResumer$.subscribe((track) => {
      if (track) {
        this.trackingEntryInCourse = track
        this.timeEntrySelected = timeEntriesTypes.TIMER
      }
    })

  }

  ngAfterViewInit(): void {
    // Force timer restart if needed
    if (this.trackingEntryInCourse?.isOngoing()) {
      // Use setTimeout to ensure the view is fully initialized
      setTimeout(() => {
        if (this.timeEntryPlayer) {
          this.timeEntryPlayer.restartTimerIfNeeded();
          this.cdr.detectChanges(); // Force change detection
        }
      }, 0);
    }
  }

  @ViewChild(TimeEntryPlayerComponent) timeEntryPlayer?: TimeEntryPlayerComponent;

  ngOnInit(): void {
    let user: string;

    // First get current user
    this.userRep.getCurrentUser().subscribe((userRes) => {
      this.userId = userRes.id;
      this.isManager = userRes.roles.includes(Roles.chronos_manager)

      // Then set up filters with user ID
      this.filters.setTimeTracksFilters({
        authors: [userRes.id],
      }, 'hard');
      //
      user = userRes.id
    });

    this.form.valueChanges.subscribe((changes) => {
      const users = this.isManager ? changes.members || [user] : [user];

      this.filters.saveFormValues('trackerFilters', changes);

      this.filters.setTimeTracksFilters({
        title: changes.title || undefined,
        authors: users.length || changes.title ? users : [user],
      });
    });
  }

  selectTimeEntry(timeEntry: string | null) {
    this.timeEntrySelected = timeEntry as timeEntriesTypes
  }

  onEnterPressed(entry: TimeTrack) {
    this.store.updateEntity(entry);
    this.trackingEntryInCourse = null;
  }

  onUpdateEntries(entries: TimeTrack[]) {
    this.store.saveTimeTracks(entries);
  }


  openItemAnnihilatorDialog(
    enterAnimationDuration: string,
    exitAnimationDuration: string
  ): void {
    this.dialog.open(EntityItemsAnnihilatorComponent<TimeTrack>, {
      width: 'auto',
      height: 'auto',
      maxWidth: '80dvw',
      maxHeight: '80dvh',
      enterAnimationDuration,
      exitAnimationDuration,
      data: {
        entities: this.trackEntries,
      }
    });
  }
}
